import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ['home', 'services', 'vision-studio', 'portfolio', 'about', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const projects = [
    {
      title: 'Corporate Brand Launch',
      category: 'Corporate Events',
      description: 'Complete artistic direction for international brand launch event with live performances, themed atmosphere, and curated entertainment.',
      image: 'https://public.readdy.ai/ai/img_res/d129fe232cd080df4bed4db992ae00d9.jpg'
    },
    {
      title: 'Luxury hotel artistic direction',
      category: 'Hospitality Events',
      description: 'Artistic direction and concept creation for luxury hotel with live music, entertainment coordination, and immersive guest experience.',
      image: 'https://readdy.ai/api/search-image?query=elegant%20hotel%20event%20space%20with%20guests%20mingling%20warm%20lighting%20modern%20interior%20design%20welcoming%20atmosphere%20people%20enjoying%20celebration%20bright%20natural%20light%20sophisticated%20venue&width=600&height=800&seq=tdgbright002&orientation=portrait'
    },
    {
      title: 'Themed Restaurant Night',
      category: 'Venue Concepts',
      description: 'Creative concept and artistic direction for themed dining experience with live performers, curated ambiance, and coordinated entertainment.',
      image: 'https://readdy.ai/api/search-image?query=vibrant%20restaurant%20event%20with%20diners%20enjoying%20atmosphere%20live%20music%20performance%20warm%20ambient%20lighting%20modern%20elegant%20space%20people%20celebrating%20colorful%20decor%20welcoming%20environment&width=600&height=800&seq=tdgbright003&orientation=portrait'
    },
    {
      title: 'Private Celebration',
      category: 'Private Events',
      description: 'Bespoke event concept with artist booking, venue coordination, and complete artistic direction for exclusive private gathering.',
      image: 'https://readdy.ai/api/search-image?query=elegant%20private%20party%20with%20guests%20celebrating%20warm%20lighting%20beautiful%20venue%20setup%20people%20dancing%20and%20enjoying%20live%20entertainment%20sophisticated%20atmosphere%20natural%20bright%20tones&width=600&height=800&seq=tdgbright004&orientation=portrait'
    },
    {
      title: 'Brand Activation Experience',
      category: 'Brand Events',
      description: 'Creative direction for brand activation with interactive experiences, live performances, and curated atmosphere design.',
      image: 'https://readdy.ai/api/search-image?query=modern%20brand%20event%20with%20people%20interacting%20colorful%20displays%20bright%20lighting%20engaging%20atmosphere%20professional%20setup%20welcoming%20space%20creative%20installation%20natural%20light&width=600&height=800&seq=tdgbright005&orientation=portrait'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/3c813cd8ecc1f68cc049f111a65983de/28ccda400db57731b6954ace23c26e7f.png" alt="TDG Logo" className="h-14 w-auto" />
          </div>
          <div className="flex items-center gap-10">
            {[
              { id: 'home', label: 'Home' },
              { id: 'services', label: 'Services' },
              { id: 'vision-studio', label: 'Vision Studio' },
              { id: 'portfolio', label: 'Portfolio' },
              { id: 'about', label: 'About' },
              { id: 'contact', label: 'Contact' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium tracking-wide transition-all duration-300 hover:text-teal-600 whitespace-nowrap ${
                  activeSection === item.id ? 'text-teal-600' : 'text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/3c813cd8ecc1f68cc049f111a65983de/b1c49b950589d11e4862aa212ebbb49a.jpeg" 
            alt="TDG Events" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-white/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-8 max-w-6xl mx-auto">
          <h1 className="text-7xl md:text-8xl font-bold text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            TDG
          </h1>
          <p className="text-xl text-gray-800 tracking-[0.3em] mb-8 uppercase font-semibold">
            The Direction Group
          </p>
          <p className="text-xl md:text-3xl text-gray-900 mb-16 leading-relaxed max-w-4xl mx-auto font-light">
            Creative Event Agency / Artistic Direction / Concept Creation / Entertainment
          </p>
          <button 
            onClick={() => scrollToSection('services')}
            className="group inline-flex items-center bg-teal-600 rounded-lg px-10 py-5 hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
          >
            <span className="text-white font-semibold tracking-wide text-sm mr-3">Discover Our Services</span>
            <i className="ri-arrow-right-line text-white text-lg"></i>
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-gray-50 py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <p className="text-xs uppercase tracking-[0.25em] text-teal-600 font-bold">Who We Are</p>
              <h2 className="text-5xl leading-tight text-gray-900 font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Event Agency Specialized in <strong className="text-teal-600">Artistic Direction</strong> and <strong className="text-teal-600">Concept Creation</strong>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                TDG – The Direction Group is an event agency based in Agadir Morocco, working locally and internationally.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We create and design <strong className="text-gray-900">event concepts</strong>, imagine <strong className="text-gray-900">atmospheres</strong>, and coordinate the <strong className="text-gray-900">artistic direction</strong> of each project according to our clients' needs and identity.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We work with <strong className="text-gray-900">brands, hotels, restaurants, companies, and private clients</strong> to create engaging and memorable experiences.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://static.readdy.ai/image/3c813cd8ecc1f68cc049f111a65983de/463b06c11cfbd91765eda33a59d71a5c.jpeg" 
                  alt="TDG Team" 
                  className="w-full h-[600px] object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-white py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-6xl text-gray-900 leading-tight font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Event Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept creation to artist booking, we handle every aspect of your event with creativity and professionalism.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Artistic Direction & Concept Creation',
                description: 'We create unique event concepts and coordinate the artistic direction according to your identity and objectives.',
                image: 'https://static.readdy.ai/image/3c813cd8ecc1f68cc049f111a65983de/ba0eb664f7ba905b79175f971f5871b9.png',
                items: ['Event Concept Design', 'Themed Nights Creation', 'Atmosphere Design', 'Creative Direction']
              },
              {
                title: 'Corporate & Brand Events',
                description: 'Professional event coordination for companies and brands with complete artistic direction and entertainment.',
                image: 'https://readdy.ai/api/search-image?query=modern%20corporate%20event%20with%20attendees%20networking%20bright%20venue%20colorful%20lighting%20professional%20atmosphere%20business%20gathering%20elegant%20setup%20people%20engaging&width=600&height=400&seq=tdgbright009&orientation=landscape',
                items: ['Brand Activations', 'Product Launches', 'Corporate Gatherings', 'Team Events']
              },
              {
                title: 'Artist Booking & Coordination',
                description: 'We book and coordinate musicians, singers, DJs, dancers, and live performers for engaging experiences.',
                image: 'https://readdy.ai/api/search-image?query=live%20music%20performance%20at%20event%20with%20audience%20enjoying%20bright%20colorful%20stage%20lighting%20musicians%20performing%20welcoming%20atmosphere%20people%20celebrating%20vibrant%20energy&width=600&height=400&seq=tdgbright010&orientation=landscape',
                items: ['Musicians & Singers', 'DJs & Sound', 'Dancers & Performers', 'Live Entertainment']
              }
            ].map((service, index) => (
              <div key={index} className="group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-100">
                <div className="relative h-64 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl text-gray-900 mb-4 font-bold">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    {service.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-gray-700 text-sm">
                        <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                        <span className="font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Services */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-10 rounded-2xl shadow-md">
              <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl text-gray-900 mb-4 font-bold">
                Venue Scouting & Coordination
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We help you find the perfect location for your event and coordinate all venue logistics to ensure a seamless experience.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-10 rounded-2xl shadow-md">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-hotel-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl text-gray-900 mb-4 font-bold">
                Hotels, Restaurants & Private Events
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We create concepts for hotels, restaurants, and private celebrations with complete artistic direction and entertainment coordination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Studio Section */}
      <section id="vision-studio" className="bg-gradient-to-br from-gray-50 to-blue-50 py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="text-6xl text-gray-900 leading-tight font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                TDG Vision Studio
              </h2>
              <p className="text-xl text-gray-800 leading-relaxed mb-6 font-semibold">
                Marketing & Communication Agency
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                TDG Vision Studio is our marketing and communication branch supporting hotels, restaurants, brands, architects, and lifestyle businesses.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We provide <strong className="text-gray-900">community management, social media strategy, content creation, and brand image support</strong> to help our clients grow their visibility and engagement.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20photographer%20taking%20photos%20at%20event%20bright%20modern%20venue%20people%20working%20with%20camera%20content%20creation%20natural%20lighting%20welcoming%20atmosphere%20creative%20production&width=700&height=500&seq=tdgbright011&orientation=landscape" 
                alt="Vision Studio" 
                className="w-full h-[500px] object-cover object-center rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Community Management',
                description: 'Social media strategy and daily management for your brand.',
                icon: 'ri-chat-smile-3-line'
              },
              {
                title: 'Content Creation',
                description: 'Creative content for social media and digital platforms.',
                icon: 'ri-edit-box-line'
              },
              {
                title: 'Photo & Video Production',
                description: 'Professional photography and videography for events and brands.',
                icon: 'ri-camera-3-line'
              },
              {
                title: 'Brand Image Support',
                description: 'Strategic support to enhance your brand visibility.',
                icon: 'ri-star-line'
              },
              {
                title: 'Event Coverage',
                description: 'Complete photo and video coverage of your events.',
                icon: 'ri-video-line'
              },
              {
                title: 'Social Media Strategy',
                description: 'Tailored strategies to grow your online presence.',
                icon: 'ri-line-chart-line'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl text-gray-900 mb-3 font-bold">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="bg-white py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-6xl text-gray-900 font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Work
            </h2>
            <p className="text-xl text-gray-600">Selected projects showcasing our creative direction and event expertise.</p>
          </div>

          <div className="overflow-x-auto pb-8">
            <div className="flex gap-8" style={{ width: 'max-content' }}>
              {projects.map((project, index) => (
                <div 
                  key={index} 
                  className="relative overflow-hidden group cursor-pointer rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
                  style={{ width: '480px', height: '640px' }}
                  onClick={() => setSelectedProject(index)}
                >
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <p className="text-xs text-teal-400 uppercase tracking-[0.25em] mb-3 font-bold">{project.category}</p>
                    <h3 className="text-3xl text-white font-bold mb-2">{project.title}</h3>
                    <div className="h-1 w-16 bg-teal-500 mt-4 group-hover:w-32 transition-all duration-500 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject !== null && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedProject(null)}
        >
          <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-[600px]">
              <img 
                src={projects[selectedProject].image} 
                alt={projects[selectedProject].title} 
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="flex flex-col justify-center p-12">
              <p className="text-xs text-teal-600 uppercase tracking-[0.25em] mb-4 font-bold">{projects[selectedProject].category}</p>
              <h3 className="text-5xl text-gray-900 font-bold mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                {projects[selectedProject].title}
              </h3>
              <p className="text-xl text-gray-700 leading-relaxed mb-12">
                {projects[selectedProject].description}
              </p>
              <button 
                onClick={() => setSelectedProject(null)}
                className="self-start inline-flex items-center gap-3 text-gray-900 hover:text-teal-600 transition-colors cursor-pointer bg-gray-100 px-6 py-3 rounded-lg hover:bg-gray-200"
              >
                <i className="ri-close-line text-2xl"></i>
                <span className="text-sm uppercase tracking-wider font-semibold">Close</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Why TDG */}
      <section className="bg-gray-50 py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl text-gray-900 mb-6 font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Choose TDG
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              {
                title: 'Creative Expertise',
                description: 'Specialized in artistic direction and concept creation for unique events.',
                icon: 'ri-lightbulb-flash-line'
              },
              {
                title: 'Complete Service',
                description: 'From concept to execution, including artist booking and venue coordination.',
                icon: 'ri-checkbox-multiple-line'
              },
              {
                title: 'Marketing Integration',
                description: 'Event creation combined with marketing and content production through Vision Studio.',
                icon: 'ri-links-line'
              },
              {
                title: 'Local & International',
                description: 'Based in Morocco with experience working locally and internationally.',
                icon: 'ri-global-line'
              }
            ].map((feature, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl text-gray-900 mb-4 font-bold">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=bright%20modern%20event%20venue%20with%20people%20celebrating%20warm%20colorful%20lighting%20welcoming%20atmosphere%20professional%20gathering%20elegant%20space%20happy%20guests%20enjoying%20party%20creative%20setup%20natural%20light&width=1920&height=1080&seq=tdgbright012&orientation=landscape" 
            alt="Work with TDG" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-white/50"></div>
        </div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <h2 className="text-6xl text-gray-900 font-bold mb-10 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Let's Create Your Next Event
          </h2>
          <p className="text-2xl text-gray-800 mb-12 font-light">
            Contact us to discuss your project and bring your vision to life.
          </p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="inline-flex items-center bg-teal-600 rounded-lg px-10 py-5 hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-xl"
          >
            <span className="text-white font-semibold tracking-wide text-sm mr-3">Get In Touch</span>
            <i className="ri-arrow-right-line text-white text-lg"></i>
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-gradient-to-br from-gray-900 to-gray-800 py-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16">
            {/* Brand */}
            <div>
              <img src="https://static.readdy.ai/image/3c813cd8ecc1f68cc049f111a65983de/28ccda400db57731b6954ace23c26e7f.png" alt="TDG Logo" className="h-16 w-auto mb-8 brightness-0 invert" />
              <p className="text-xl text-gray-300 font-light leading-relaxed">
                Creating memorable events and experiences
              </p>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-sm text-white font-bold uppercase tracking-[0.2em] mb-8">Services</h4>
              <div className="space-y-4">
                {['Event Concepts', 'Artistic Direction', 'Artist Booking', 'Vision Studio', 'Content Production'].map((link, index) => (
                  <button 
                    key={index}
                    onClick={() => scrollToSection('services')}
                    className="block text-gray-400 hover:text-teal-400 transition-colors text-left text-sm cursor-pointer"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>

            {/* Connect */}
            <div>
              <h4 className="text-sm text-white font-bold uppercase tracking-[0.2em] mb-8">Connect</h4>
              <div className="space-y-4">
                <a 
                  href="https://www.instagram.com/tdg.directiongroup/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 hover:text-teal-400 transition-colors text-sm"
                >
                  <i className="ri-instagram-line text-lg"></i>
                  <span>TDG Direction Group</span>
                </a>
                <a 
                  href="https://www.instagram.com/tdg.agency/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 hover:text-teal-400 transition-colors text-sm"
                >
                  <i className="ri-instagram-line text-lg"></i>
                  <span>TDG Vision Studio</span>
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-sm text-white font-bold uppercase tracking-[0.2em] mb-8">Contact</h4>
              <div className="space-y-6">
                <div>
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">Phone</p>
                  <a href="tel:+212777781844" className="text-white hover:text-teal-400 transition-colors text-sm">
                    +212 7 77 78 18 44
                  </a>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">Events</p>
                  <a href="mailto:tdg.thedirectiongroup@gmail.com" className="text-white hover:text-teal-400 transition-colors text-sm break-all">
                    tdg.thedirectiongroup@gmail.com
                  </a>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">Marketing</p>
                  <a href="mailto:tdg.visionstudio@gmail.com" className="text-white hover:text-teal-400 transition-colors text-sm break-all">
                    tdg.visionstudio@gmail.com
                  </a>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">Location</p>
                  <p className="text-white text-sm">Morocco — Worldwide</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-20 pt-10 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">© 2025 TDG – The Direction Group</p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-teal-400 transition-colors uppercase tracking-wider"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
